import React, {useState} from 'react';
import {Text, View} from 'react-native';
import {useSelector} from 'react-redux';
import {images} from '../../../assets/icons';
import {contentSelector} from '../../../redux/selectors/resources';
import {Checkbox} from '../../../shared/components/baseField/components/checkbox/checkbox';
import {Button} from '../../../shared/components/button';
import {ExternalLink} from '../../../shared/components/externalLink';
import {Hr} from '../../../shared/components/hr';
import {Markdown} from '../../../shared/components/markdown';
import {ResponsiveView} from '../../../shared/components/responsiveView';
import {useStyles} from '../../../shared/hooks/styles';
import {useIsDesktop} from '../../../shared/hooks/useIsDesktop';
import {useTheme} from '../../../shared/theme';
import {DeviceSize} from '../../../shared/theme/helpers';

const skipassFeatures = [
  {
    title: 'История поездок',
    description: 'Отслеживайте, сколько раз спустились.',
  },
  {
    title: 'История операций',
    description: 'Храните все в одном месте.',
  },
  {
    title: 'Блокировка',
    description: 'Если потеряли карту – просто заблокируйте доступ к ней.',
  },
];

interface NotRegisteredSkipassProps {
  onRegisterPress: () => void;
}

export function NotRegisteredSkipass({onRegisterPress}: NotRegisteredSkipassProps) {
  const isDesktop = useIsDesktop();
  const content = useSelector(contentSelector());
  const theme = useTheme();

  const [isAccepted, setIsAccepted] = useState(false);

  const styles = useStyles(({mediaOnly}) => ({
    featuresList: {
      width: '100%',
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 10,
    },
    contentTextWrapper: {
      ...mediaOnly(DeviceSize.desktop, {maxWidth: '80%'}),
      ...mediaOnly(DeviceSize.mobile, {marginBottom: 20}),
    },
    checkboxContainer: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      marginVertical: isDesktop ? 24 : 0,
      marginHorizontal: 0,
      '&:hover': {
        cursor: 'pointer',
      },
    },
    checkboxBtn: {
      marginRight: 12,
    },
    agreement: {
      flexShrink: 1,
      flexGrow: 1,
    },
    agreementText: {
      ...mediaOnly(DeviceSize.desktop, theme.fonts.body, {
        ...theme.fonts.bodySmallLight,
        lineHeight: 24,
      }),
    },
    agreementLink: {
      color: theme.color.primaryColor,
      ...mediaOnly(DeviceSize.desktop, theme.fonts.body, {
        ...theme.fonts.bodySmallLight,
        lineHeight: 24,
      }),
    },
    registerButton: {
      paddingHorizontal: 10,
      height: 'auto',
      marginTop: 24,
      ...mediaOnly(DeviceSize.mobile, {width: '100%', paddingVertical: 10}, {width: '35%', paddingVertical: 18}),
      ...mediaOnly(DeviceSize.desktop, {marginTop: 0}),
    },

    bottomContainer: {
      marginTop: 40,
    },

    featureList: {
      marginTop: 30,
      marginBottom: 30,
      ...mediaOnly(DeviceSize.mobile, {
        marginTop: 0,
        marginBottom: 0,
      }),
    },

    feature: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      ...theme.fonts.label,
    },

    featureTitle: {
      marginBottom: 5,
      color: theme.color.textColor,

      ...mediaOnly(
        DeviceSize.desktop,
        {
          ...theme.fonts.h5,
        },
        {
          ...theme.fonts.bodySmall,
        },
      ),
    },
    featureDescription: {
      ...mediaOnly(
        DeviceSize.desktop,
        {
          ...theme.fonts.h5Light,
          color: theme.color.textColor,
        },
        {
          ...theme.fonts.bodySmallLight,
          width: '100%',
        },
      ),
    },

    featureIcon: {
      marginRight: 10,
      flexShrink: 0,
    },
  }));

  return (
    <View>
      <View style={styles.contentTextWrapper}>
        <Markdown listColumns={1} textStyle={{fontWeight: 'bold'}}>
          {content.services.skipass.titleMarkdown}
        </Markdown>
        <Markdown listColumns={1}>{content.services.skipass.descriptionMarkdown}</Markdown>
      </View>

      <View style={styles.featureList}>
        {skipassFeatures.map(({title, description}, index) => (
          <View key={index} style={[styles.feature, index !== skipassFeatures.length - 1 && {marginBottom: 30}]}>
            <View style={styles.featureIcon}>
              <images.Check color={theme.color.primaryColor} width={28} height={28} />
            </View>
            <View style={{flex: 1}}>
              <Text style={styles.featureTitle}>{title}</Text>
              <Text style={styles.featureDescription}>{description}</Text>
            </View>
          </View>
        ))}
      </View>

      <ResponsiveView only={[DeviceSize.mobile]}>
        <Hr />
      </ResponsiveView>

      <View style={styles.checkboxContainer}>
        <Checkbox
          style={styles.checkboxBtn}
          testIDprefix='skipass-agreement-checkbox'
          value={isAccepted}
          onChange={() => setIsAccepted((prev) => !prev)}
        />
        <Text style={styles.agreement}>
          <Text style={styles.agreementText}>Я даю</Text>{' '}
          <ExternalLink
            testID='agreeemnet-external-id'
            style={styles.agreementLink}
            link={content.services.skipass.agreementLink}
            text='согласие на обработку персональных данных '
            fileName={'eskso_agreement'}
            showLoadingText={false}
            target='_blank'
          />
          <Text style={styles.agreementText}>в соответствии с</Text>
          <ExternalLink
            testID='privacy-policy-external-id'
            style={styles.agreementLink}
            link={content.docs.politics}
            text={' политикой конфиденциальности '}
            fileName={'eskso_privacy_policy'}
            showLoadingText={false}
            target='_blank'
          />
        </Text>
      </View>

      <Button
        id='skipass-register-btn'
        text='Зарегистрироваться'
        disabled={!isAccepted}
        onPress={onRegisterPress}
        style={styles.registerButton}
      />
    </View>
  );
}
